import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import {GestureEventListeners} from '@polymer/polymer/lib/mixins/gesture-event-listeners';
import {UserService} from '@nuskin/ns-account';
import {Translations} from './translations.js';
import {TranslationsUtil, ConfigService, NsRestError, moment, GoogleMapsAPILoader} from '@nuskin/ns-util';
import {ShippingService, PickupUtil, ShippingAddressAdapter} from '@nuskin/ns-shop';
import TranslationMixin from '../temp/ns-translations';
import template from './ns-shipping.html';
import {shippingDataObject} from './test-shipping-object.js';
import {parseIntRange, createProcessingDateList, createDateList} from './ns-shipping.service';
import '../temp/ns-form-temp';
import SHIPPING_ADDRESS_CONFIG from '../temp/shipping-address-config';
import { getConfiguration } from '@nuskin/configuration-sdk';

const BRAND_AFFILIATE = '10'

/**
 * Image Imports
 */
import closeIcon from '../img/ic-close-small.svg';
import infoIcon from '../img/info-icon-blue.svg';
import warningIcon from '../img/warning-icon.svg';
import backgroundAlert from '../img/background-alert.svg';

/**
 * Component used to manage a users list of associated address. Used by Profile and ECOM teams.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>Shipping Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-shipping is-demo></ns-shipping>
 *   </template>
 * </demo-snippet>
 *
 * @demo elements-app/src/ns-button/docs.html
 * @defaultText Example button
 */
class NsShipping extends TranslationMixin(GestureEventListeners(PolymerElement)) {
  static get is () {
    return 'ns-shipping';
  }

  /**
   * Sets the html template
   */
  static get template () {
    let htmlElement = document.createElement('TEMPLATE');
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  /**
   * Sets properties to pass to this component
   */
  static get properties () {
    return {
      /**
       * Object used to share data between ns-shipping and ns-adp-wrapper.
       * @type {ShippingDataObject}
       */
      shippingDataObject: {
        type: Object,
        notify: true,
        observer: 'showShippingInformation'
      },
      /**
       * List of saved shipping addresses
       * @type {ShippingAddressDTO[]}
       */
      addresses: {
        type: Array,
        value: () => []
      },
      /**
       * Country selected
       */
      countryCode: {
        type: String,
        observer: '_initialize'
      },
      /**
       * Language selected
       */
      language: {
        type: String
      },
      /**
       * index of current shipping method in list
       */
      idxOfShippingMethod: {
        type: Number,
        observer: '_onShippingMethodIdxChanged'
      },
      /**
       * Order frequency for current ADR
       */
      frequency: {
        type: Number
      },
      /**
       * Footer message computed based on process date and frequency
       */
      shipFooterMessage: {
        type: String,
        computed: '_computeProcessDate(shippingDataObject.nextDeliveryDate, shippingDataObject.frequency)'
      },
      /**
       * Array of allowed frequency for dropdown.
       */
      frequencies: {
        type: Array
      },
      /**
       * Array of allowed date options for ADR processing.
       * Populates the processing date selection dropdown.
       */
      processDateOptions: {
        type: Array
      },
      /**
       * Processing date timestamp selected in the processing date selection dropdown.
       */
      selectedProcessTimestamp: {
        Type: Number,
        observer: '_onSelectProcessingDate'
      },
      /**
       * Processing date timestamp selected in the processing date selection dropdown.
       */
       shipFromAddressName: {
        Type: String,
        observer: '_onSelectShipFromAddressName'
      },

      /**
       * Processing date timestamp to include in ADR update request.
       */
      requestedProcessTimestamp: {
        type: Number
      },
      /**
       * Used for switching views: Shipping Information, Shipping Address List, Shipping Address Form
       */
      view: {
        type: String
      },
      /**
       * Used to toggle showing of default address checkbox
       */
      hideAddressSaveCheckbox: {
        type: Boolean,
        value: false
      },
      /**
       * Used to toggle showing of save address checkbox
       */
      hideAddressDefaultCheckbox: {
        type: Boolean,
        value: false
      },
      /**
       * List of available shipping methods
       * @type {ShippingMethod[]}
       * @private
       */
      shippingMethods: {
        type: Array,
        value: []
      },
      /**
       * Selected shipping method
       * @type {ShippingMethod}
       * @private
       */
      currentShippingMethod: {
        type: Object,
        value: null
      },
      /**
       * Disables buttons and functionality if adr is not editable
       */
      isNotEditable: {
        type: Boolean,
        value: false
      },
      /**
       * Encoded value of close icon used for passing this value to the template
       */
      closeIcon: {
        type: String,
        value: closeIcon
      },
      /**
       * Encoded value of info icon used for passing this value to the template
       */
      infoIcon: {
        type: String,
        value: infoIcon
      },
      /**
       * Page context for components
       * e.g. 'Adp Manage' or 'Profile'
       */
      pageName: {
        type: String
      },
      /**
       * Encoded value of warning icon used for passing this value to the template
       */
      warningIcon: {
        type: String,
        value: warningIcon
      },
      /**
       * Encoded value of warning icon used for passing this value to the template
       */
      backgroundAlert: {
        type: String,
        value: backgroundAlert
      },
      /**
       * Allows disabling all delete buttons, for testing purposes.
       */
      enableDelete: {
        type: Boolean,
        value: true
      },
      /**
       * Populates component with test data and prevents any unnecessary network calls.
       */
      isDemo: {
        type: Boolean,
        value: false
      },
      /**
       * Setting this to true gives the custom attribute to ns-buttons contained within ns-shipping.
       * This allows for style overrides via a style module.
       */
      useCustomButtons: {
        type: Boolean,
        value: false
      },
      /**
       * Page upon which this component appears. Used for translations.
       */
      webPageName: {
        type: String,
        value: 'shop/adp-manage'
      },
      /**
       * Translations from the server. Triggers the translation assignment process.
       * If undefined and shouldAwaitTranslations=false, this component will fetch its own strings.
       */
      serverTranslations: {
        type: Object,
        observer: '_assignTranslations'
      },
      /**
       * If false, it will start the translation assignment process without waiting.
       * If true, it will wait for serverTranslations to be set before starting the translation assignment process.
       * If serverTranslations is defined already, the component will behave as if this property is true.
       */
      shouldAwaitTranslations: {
        type: Boolean,
        value: false
      },
      /**
       * Collection of toast message objects, shared with the parent (ns-adp-wrapper).
       * Messages in this collection are displayed by the parent.
       */
      toastMessages: {
        type: Array,
        notify: true
      },
      processDateSelectorPristine: {
        type: Boolean,
        value: true
      },
      /**
       * @type {number[]}
       */
      shippingMethodRestrictions: {
        type: Array,
        value: () => []
      },
      isValidShippingDate: {
        type: Boolean,
        value: true
      },
      showPickUpOptions: {
        type: Boolean,
        value: false,
        observer: '_onSetShowPickupOptions'
      },
      /**
       * @type {PickupPoint[]}
       */
      pickUpOptions: {
        type: Array,
        value: () => [],
        observer: '_onChangePickUpOptions'
      },
      selectedPPTab: {
        type: Number,
        value: 1,
        observer: '_onChangePPTab'
      },
      /**
       * Google Map instance for use with EMEA Pickup Points
       */
      pickupPointsMap: {
        type: Object,
        value: null
      },
      /**
       * List of Google Map Markers on Pickup Points map
       * @type {PickupPointMarker[]}
       */
      mapMarkers: {
        type: Array,
        value: () => []
      },
      /**
       * Google Map Info Window on Pickup Points map
       */
      mapInfoWindow: {
        type: Object,
        value: null
      },
      /**
       * The "SAVE" button is disabled if `true`.
       */
      // saveBtnDisabled: {
      //   type: Boolean,
      //   value: false,
      //   observer: "_onSetSaveBtnDisabled"
      // },
      /**
       * holder for the feature flags enabled for the user
       */
      featureFlags: {
        type: Object,
        value: {},
        observer: "_featureFlagsUpdated"
      },
      /**
       * flag for whether or not to sort state list
       */
      sortStateList: {
        type: Boolean,
        value: true
      },
      shouldShipFromNameDisplay: {
        type: Boolean,
        value: false
      },
      /**
       * flag for whether or not to use server translations in shipping
       */
       useServerTranslationsForShipping: {
         type: Boolean,
         value: false
       },

       selectedValidationCandidateIndex: {
         type: Number,
         observer: "_selectedValidationCandidateIndexChanged"
       },

       /**
       * Property to toggle service call loader, ns-global-loader component
       */
      showLoader: {
        type: Boolean,
        notify: true
      }
    };
  }

  ready () {
    super.ready();
    this._initialize();
    GoogleMapsAPILoader.load();
  }

  /**
   * Sets the account id, and gets the address list needed for shipping address list display
   */
  _initialize () {
    this.isPickUp = false;
    this.shippingService = ShippingService;
    if (!this.TRANSLATION_HELPER) this._localize();
    this._setConfigurations();
    const user = UserService.getUser()

    if (this.showShipFromName) {
      this.shouldShipFromNameDisplay = true;
      const onlyShowForBrandAffiliates =
        !!ConfigService.getMarketConfig().adpManage.showShipFromNameOnlyForBrandAffiliates;
      const isBrandAffiliate = user && user.accountType === BRAND_AFFILIATE
      if (onlyShowForBrandAffiliates && !isBrandAffiliate) {
        this.shouldShipFromNameDisplay = false;
      }
    }

    if (!this.isDemo) {
      this.initializeShipping();
    } else {
      /**
       * @type {ShippingDataObject}
       */
      this.shippingDataObject = shippingDataObject;
      this.blackoutDays = [1, 4, 6, 27];
      this.set('addresses', shippingDataObject['shipping']['shipping']['ShippingParty']['ShippingAddressList']);
    }
  }

  static get observers() {
    return [
      '_checkShippingType(selectedShippingTypeValue)',
      '_setUpPickUpOptions(shippingDataObject.pickUpOptions)',
      '_pickUpOptionSelectedChanged(shippingDataObject.pickUpOptionSelected, currentShippingMethod)'
    ];
  }

  /**
   * Observer for pickUpOptions.
   * @param {PickupPoint[]} pickUpOptions
   */
  _setUpPickUpOptions (pickUpOptions) {
    this.pickUpOptions = pickUpOptions;
  }

  /**
   * Observer for pickUpOptionSelected.
   * Set showSelectedPickUpPoint flag
   * Show Selected Pick Up Point if:
   * the user has a selected pick up point and the ship method is a supported Pickup Point method
   */
  _pickUpOptionSelectedChanged () {
    this.showSelectedPickUpPoint = !!this.pickUpOptionSelected && PickupUtil.supports(this.currentShippingMethod.Code);
  }
  /**
   * Sets configurations to customize functionality for the current market
   */
  _setConfigurations () {
    getConfiguration(['Checkout'])
      .then((config) => {
        this.useShipMethodsApi = config.Checkout.useShipMethodsApi;
      });

    const SERVICE = ConfigService.getMarketConfig().adpManage;
    const DEFAULT = {
      'hideShippingFrequency': false,
      'showShippingPickupLocation': true,
      'hideProcessingDateDropdown': false,
      'shippingMethodRestrictions': [],
      'showMonthAsNumber': false,
      'showDateAsOrdinal' : false,
      'sortStateList' : true,
      'useServerTranslationsForShipping' : false,
      'useAddressValidationService' : false
    };
    const CONFIG = Object.assign({}, DEFAULT, SERVICE);
    this.hideShippingFrequency = CONFIG.hideShippingFrequency;
    this.showShippingPickupLocation = CONFIG.showShippingPickupLocation;
    this.hideProcessingDateDropdown = CONFIG.hideProcessingDateDropdown;
    this.displayBiMonthlyPopup = CONFIG.displayBiMonthlyPopup;
    this.displayBiMonthlyFrequency = CONFIG.displayBiMonthlyFrequency;
    this.showMonthAsNumber = CONFIG.showMonthAsNumber && this.featureFlags.cx14_1550_use_month_number;
    this.showDateAsOrdinal = CONFIG.showDateAsOrdinal;
    this.sortStateList = CONFIG.sortStateList;
    this.showShipFromName = CONFIG.showShipFromName;
    this.useServerTranslationsForShipping = CONFIG.useServerTranslationsForShipping;
    this.isInvalidShippingDateDialogDisabled = CONFIG.disableInvalidShippingDateDialog;
    this.allowOnlyBAsToEditShippingName = !!CONFIG.allowOnlyBAsToEditShippingName;
    this.usernameIsDefaultShippingName  = !!CONFIG.usernameIsDefaultShippingName;
    this.useAddressValidationService = CONFIG.useAddressValidationService;
    try {
      /**
       * @type {{code: string, dates: number[]}[]}
       */
      this.shippingMethodRestrictions = (CONFIG.shippingMethodRestrictions instanceof Array
        ? CONFIG.shippingMethodRestrictions
        : [CONFIG.shippingMethodRestrictions])
        .map(JSON.parse)
        .map(r => ({ ...r, dates: parseIntRange(r.dates) }));
    } catch (e) {
      console.error(e);
      this.shippingMethodRestrictions = [];
    }
  }

  /**
   * Returns boolean based on pageName
   */
  _isPageNameProfile () {
    return this.pageName === 'Profile';
  }

  /**
   * Sets language and country properties and prepares translations
   */
  _localize () {
    this.TRANSLATION_HELPER = new TranslationsUtil(this.language, this.country, Translations);
    this.language = this.TRANSLATION_HELPER.language;
    this.country = this.TRANSLATION_HELPER.country;
    if (!this.shouldAwaitTranslations) this._assignTranslations();
  }

  /**
   * Observer on serverTranslations that gathers all translations and maps them to properties.
   * Any strings set as properties and defined in the html attributes take precedence over translations from the server,
   * which in turn take precedence over the default translations in the translations file.
   */
  async _assignTranslations () {
    if (!this.TRANSLATION_HELPER) this._localize();
    this.serverTranslations =
      this.serverTranslations || await this.TRANSLATION_HELPER.getServerTranslations(this.webPageName);
    let allTranslations = Object.assign({}, this.TRANSLATION_HELPER.getDefaultTranslations(), this.serverTranslations);
    Object.entries(allTranslations).forEach(([key, value]) => {
      this[key] = this[key] || value;
    });
  }

  initializeShipping () {
    this.view = 'Shipping Information';
    if (this.pageName === 'Profile') {
      this.showShippingAddressList();
    } else {
      this.adrBlackoutPeriod = 1 // default value, used when ship days does not return a valid response.;
      // default value, used when ship days does not return a valid response.
      this.adrDisabledDates = [1, 26, 27, 28, 29, 30, 31];
      ShippingService.getShipDays()
        .then(response => {
          let adrShipDays = response.ShipDaysResponse;
          this.adrBlackoutPeriod = adrShipDays.blackoutPeriod;
          this.adrDisabledDates = adrShipDays.blackoutDays;
        })
        .catch(console.error);
    }
  }

  getShippingMethodIdx () {
    let idx = this.shippingMethods.findIndex(method => {
      if (method.Code !== this.currentShippingMethod.Code) return false;
      if (method.Code === 'PU' || method.Code === 'FP' || method.Code === 'M3')
        return method.PickUpLocation === this.currentShippingMethod.PickUpLocation;
      return true;
    });
    return (idx > -1) ? idx : 0;
  }

  /**
   * Sets the view to Shipping Information
   */
  showShippingInformation () {
    if (this.shippingDataObject) {
      this.shipping = this.shippingDataObject.shipping['shipping'];
      if (this.shipping) {
        this.shippingParty = this.shipping['ShippingParty'] || [];
        let shipToObject;
        if (this.shipping.ShippingMethod.Code === 'PU' || this.shipping.ShippingMethod.Code === 'M3') {
          shipToObject = this.shippingParty.find(object => object['Type'] === 'FTD_SHIP_TO');
        }
        if (!shipToObject) {
          shipToObject = (this.shippingParty.find(a => a['Type'] === 'SHIP_TO') || {});
        }
        this.shippingAddress = shipToObject['ShippingAddress'];
        this.address = (this.shippingAddress || {})['Address'];
        this._setADRValues();
        this.pickUpOptions = this.shippingDataObject.pickUpOptions;
        this.pickUpOptionSelected = this.shippingDataObject.pickUpOptionSelected || this.pickUpOptionSelected;
        this.idxOfShippingMethod = this.getShippingMethodIdx();
        this.showPickUpOptions = false;
        if (this.autoShowPickupPoints) {
          this.showShippingAddressList();
          this.autoShowPickupPoints = false;
          setTimeout(() => {
            this._onSelectChangePP();
          }, 10);
        } else {
          this.view = 'Shipping Information';
        }
      }
    }
  }

  /**
   * Sets the view to Shipping Address List
   */
  showShippingAddressList () {
    this._clearToastMessages();
    if (this.pageName !== 'Profile') {
      this.tempFrequency = this.frequency;
      this.tempShippingMethod = this.currentShippingMethod;
      this._setADRValues();
      this.idxOfShippingMethod = this.getShippingMethodIdx();
    }
    if (!this.isDemo) {
      let shippingCountryList = SHIPPING_ADDRESS_CONFIG[this.country].countryList;

      let countryCode;
      if (shippingCountryList && shippingCountryList.length > 0)
        countryCode = shippingCountryList.map(x => x.value).join(',');

      let listPromise = countryCode ?
        this.shippingService.getShippingAddressList(countryCode) :
        this.shippingService.getShippingAddressList();

      listPromise
        .then((result) => {
          const addresses = result.ShippingAddressList;
          addresses.forEach(a => a.isSavedAddress = true);
          const shipToParty = this.shippingDataObject.shipping.shipping.ShippingParty.find(p => p.Type === "SHIP_TO");
          //don't want to add in a ship to party reference in the event the shipping method is a pick up location
          if (shipToParty && (!this.isPickUp || addresses.length === 0)) {
            /**
             * @type {ShippingAddressDTO}
             */
            const adrAddress = ShippingAddressAdapter.mapShippingPartyToShippingAddress(shipToParty);
            const matchingAddress = addresses.find(a => shippingAddressesMatch(a, adrAddress));
            if (!matchingAddress) {
              // No matching address found in saved addresses. Mark address to this effect and add to list.
              adrAddress.isSavedAddress = false;
              addresses.unshift(adrAddress);
            }
          }
          this.set('addresses', result.ShippingAddressList);
        })
        .catch(err => {
          this._handleError(err);
        });
    }
    this.ship = this;
    this.view = 'Shipping Address List';
    this.shadowRoot.querySelector('h2').scrollIntoView({behavior: 'smooth'});
  }

  /**
   * Sets the view to Shipping Address List and assigns the address
   * @param {ShippingAddressDTO} address
   */
  showShippingAddressForm (address) {
    this.view = 'Shipping Address Form';
    const $nsShippingAddress = this.shadowRoot.querySelector('ns-shipping-address');
    if ($nsShippingAddress) $nsShippingAddress.clearForm();
    this.shadowRoot.querySelector('h2').scrollIntoView({behavior: 'smooth'});
    this._clearToastMessages();
    //need to set to undefined to correctly set the area code ns-slotted-dropdown
    this.shippingPhoneCountry = undefined;
    //validating to see if this is an existing address or if this event was called by the new address button.
    if (address.hasOwnProperty('beShippingAddressId')) {
      this.beShippingAddressId = address.beShippingAddressId;
      this.shippingAlias = address.shippingAlias;
      this.shippingAddressName = address.shippingAddressName;
      this.shippingAddress1 = address.shippingAddress1;
      this.shippingAddress2 = address.shippingAddress2;
      this.shippingCity = address.shippingCity;
      this.shippingState = address.shippingState;
      this.shippingPostalCode = address.shippingPostalCode;
      this.shippingCountryCode = address.shippingCountry || this.countryCode;
      this.shippingPhoneCountry = address.shippingPhoneCountry || this.country;
      this.shippingPhone = address.shippingPhone;
      this.shippingMobilePhoneCountry = address.shippingMobilePhoneCountry;
      this.shippingMobilePhone = address.shippingMobilePhone;
      this.shippingEmail = address.shippingEmail;
      this.shippingCounty = address.shippingCounty;
      this.shippingInstructions = address.shippingInstructions;
      this.isDefaultShipping = address.isDefaultShipping;
      this.isSavedAddress = address.isSavedAddress;
    } else {
      //need to set to empty strings instead of undefined for correct validation after the polymer object has already been initialized.
      this.beShippingAddressId = '';
      this.shippingAlias = '';
      this.shippingAddressName = this.usernameIsDefaultShippingName ? UserService.getUser().getDisplayName() : '';
      this.shippingAddress1 = '';
      this.shippingAddress2 = '';
      this.shippingCity = '';
      this.shippingState = '';
      this.shippingPhoneCountry = this.country;
      this.shippingPostalCode = '';
      this.shippingCountryCode = this.countryCode;
      this.shippingPhone = '';
      this.shippingMobilePhoneCountry = '';
      this.shippingMobilePhone = '';
      this.shippingEmail = '';
      this.shippingCounty = '';
      this.shippingInstructions = '';
      this.isDefaultShipping = false;
      this.isSavedAddress = false;
    }
  }

  /**
   * Cancels shipping address add or edit.
   */
  _cancelShippingAddressForm () {
    this._clearToastMessages();
    this.showShippingAddressList();
  }

  /**
   * Form validation - ns-form looks for this function for validation
   */
  validate () {
    return this.shadowRoot.querySelector('ns-form-temp').validate();
  }

  /**
   * Sets ADR Detail Information and Dropdown Values
   */
  _setADRValues () {
    this._setADRShippingAddress();
    this._setADRShippingDetails();
    this._setADRShippingValues();

    // Set dropdown values
    this._getFrequencies();
    this._setProcessingDateOptions();
    this._initSelectedProcessingDate();
    this._initShippingMethods();
    this._initPickupPointMap().catch(console.error);
  }

  /**
   * Initializes the currently selected shipping address in the view from the
   * current shipping address data in the Shipping Data Object.
   */
  _setADRShippingAddress () {
    this.currentShippingAddress = this.shippingDataObject.currentShippingAddress;
    this.shipToAddressName = this.shippingDataObject.currentShippingAddressName;
    this.shipToAddress1 = this.currentShippingAddress['Street1'];
    this.shipToAddress2 = this.currentShippingAddress['Street2'];
    this.shipToCity = this.currentShippingAddress['City'];
    this.shipToCounty = this.currentShippingAddress.District;
    this.shipToState = this.currentShippingAddress['Region'];
    this.shipToPostalCode = this.currentShippingAddress['PostalCode'];
    this.shipToCountryCode = this.currentShippingAddress['CountryCode'];
    this.shipToPhoneCountry = this.shippingDataObject.shipToPhoneCountry;
    this.shipToPhone = this.shippingDataObject.shipToPhone;
    this.shipToMobilePhoneCountry = this.shippingDataObject.shipToMobilePhoneCountry;
    this.shipToMobilePhone = this.shippingDataObject.shipToMobilePhone;
    this.shipToEmail = this.shippingDataObject.shipToEmail;
    this.shipToInstructions = this.shippingDataObject.shippingInstructions;
    this.shipFromAddressName = this.shippingDataObject.shipping.senderName;
  }

  /**
   * Sets frequency, shipping method, and process date based on ADR selected
   */
  _setADRShippingDetails () {
    this.frequency = this.shippingDataObject.frequency;
    this.currentShippingMethod = this.shippingDataObject.shipping.shipping.ShippingMethod;
    this.isPickUp = this.currentShippingMethod &&
      (this.currentShippingMethod.Code === 'PU' ||
        this.currentShippingMethod.Code === 'FP' ||
        this.currentShippingMethod.Code === 'M3'
      );
    this.hideAddNewAddress = this.currentShippingMethod && this.currentShippingMethod.Code === 'FP';
    this.requestedProcessTimestamp = this.shippingDataObject.nextDeliveryDate;
  }

  /**
   * Sets frequency, shipping method, and process date values based on keys
   */
  _setADRShippingValues () {
    this.frequencyValue = this.frequency === 1 ? this.monthlyText : this.biMonthlyText;
    this.shipMethodValue = this.shippingDataObject.shipping.shipping.ShippingMethod ? this.shippingDataObject.shipping.shipping.ShippingMethod.Description : '';
    if (this.shippingDataObject.shipping.shipping.ShippingMethod.Code === 'FP') {
      this.shipMethodValue = this.shippingDataObject.shippingAvailableMethods.find(obj => obj.Code === 'FP').Description;
    }
  }

  /**
   * Saves the dropdown values (frequency, ship method, ship date) and calls the adr put service
   * @param {ShippingAddressDTO} address
   * @param {*} event
   * @param {boolean} simulate
   */
  _saveDropDownValues (address, event, simulate) {
    if (this.isDemo || this.saveBtnDisabled) {
      return;
    }

    if (this.displayBiMonthlyPopup && this.shippingDataObject.frequency === 1 && this.frequency === 2) {
      this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: false}}));
      this.$.frequencyChangeDialog.open();
    } else {
      this._updateAdrFrequencyDetails(address, simulate);
    }
  }

  /**
   * Sets the frequency of shipments and updates the address via _putAdrService
   * @param {ShippingAddressDTO} address
   * @param {boolean} simulate
   */
  _updateAdrFrequencyDetails (address, simulate) {
    this._putAdrService(address, simulate);
  }

  /**
   * Calls the adr put service to save changes to the ADR
   * @param {ShippingAddressDTO} address
   * @param {boolean} simulate
   */
  _putAdrService (address, simulate) {
    if (!this.isValidShippingDate && !this.isInvalidShippingDateDialogDisabled) {
      this._openInvalidShippingDateDialog();
      return;
    }

    this.shippingDataObject.options = {
      ShippingMethod: this.currentShippingMethod.Code,
      OrderFrequency: this.frequency,
      DeliveryDate: this.requestedProcessTimestamp,
      forceRefresh: false,
      Simulate: simulate
    };
    if (this.isPickUp) {
      this.shippingDataObject.options.PickUpLocation = this.currentShippingMethod.PickUpLocation;
    }

    if (!simulate && PickupUtil.supports(this.currentShippingMethod.Code) && this.pickUpOptionSelected) {
      const user = UserService.getUser();

      /**
       * @type {ShippingParty}
       */
      this.shippingDataObject.options.pickUpOptionSelected = {
        ShippingMemberID: user.id,
        Type: 'PICK_UP',
        ShippingAddress: {
          'Name': this.pickUpOptionSelected.name,
          'Address': {
            'Street1': this.pickUpOptionSelected.street,
            'Street2': '',
            'City': this.pickUpOptionSelected.city,
            'District': '',
            'Region': '',
            'PostalCode': this.pickUpOptionSelected.postalCode,
            'CountryCode': ''
          }
        },
        Custom: [
          {
            Key: 'TYPE',
            Value: this.pickUpOptionSelected.id,
            Type: 'PICKUPID'
          },
          {
            Key: 'TYPE',
            Value: '',
            Type: 'PICKUPSR'
          },
          ...(this.pickUpOptionSelected.carrierCode ? [{
            Key: 'TYPE',
            Value: this.pickUpOptionSelected.carrierCode,
            Type: 'CARRIER_CODE'
          }] : [])
        ]
      };
    }

    if (this.shipFromAddressName != this.shippingDataObject.shipping.senderName) {
      if (!this.shippingDataObject.options) {
        this.shippingDataObject.options = {}
      }
      if (!this.shippingDataObject.options.Custom) {
        this.shippingDataObject.options.Custom = []
      }
      this.shippingDataObject.options.Custom.push({
        Key: 'SENDER',
        Type: 'SHIP',
        Value: this.shipFromAddressName
      })
    }

    if (!address.target && !address.hasOwnProperty('Street1')) {
      this.shippingDataObject.body = this._getAdrShippingAddressBody(address);
    } else {
      this.shippingDataObject.body = this.shippingAddress;
    }

    this.set('shippingDataObject.updateType', 'Update');
  }

  /**
   * Submits the edit or add new address
   * @param {*} e
   */
  _submitTap (e) {
    // TODO-SUB-618 logic here will change to accommodate addresses saved to ADR but not to profile
    if (this.validate()) {
      if (this.useAddressValidationService) {
        this._validateAddress();
      } else {
        this._saveAddress();
      }
    }
  }

  /**
   * Breaking out the save address functionality to be called later based on validation service
   */
  _saveAddress() {
    if (this.beShippingAddressId) {
      // Update an address that is already saved to user's profile
      this.shippingService.putShippingAddress(this._getPutBody())
        .then(response => this._setShippingAddressResponse(response.ShippingAddress))
        .catch((err) => {
          this._handleError(err);
        });
    } else if (this.shouldSaveAddress) {
      // Save a new address to user's profile
      this.shippingService.postShippingAddress(this._getPostBody())
        .then(response => this._setShippingAddressResponse(response.ShippingAddress))
        .catch((err) => {
          this._handleError(err);
        });
    } else {
      // Update ADR without saving address to user's profile
      this._setShippingAddressResponse(this._getPostBody());
    }
  }

  async _validateAddress() {
    this.set("showLoader", true);
    let response = await ShippingService.useAddressValidationService(this._getValidationBody());
    this.set("showLoader", false);

    if (response.candidates && Array.isArray(response.candidates) && response.candidates.length > 0) {
      let openvalidation = true;
      this.validationCandidates = response.candidates;
      //special check for if
      if (response.candidates.length == 1) {
        let responseAddress = response.candidates[0];
        this.selectedValidationAddress = responseAddress;
        //if the validated address is the same as the address we've set, don't need to change anything
        if (this.shippingAddress1 == responseAddress.street && this.shippingAddress2 == responseAddress.street2
          && this.shippingCity == responseAddress.city && this.shippingCounty == responseAddress.district
          && this.shippingState == responseAddress.region && this.shippingPostalCode == responseAddress.postalCode) {
            openvalidation = false;
            this._saveAddress();
          }
        }
        if (openvalidation) {
        this.set("selectedValidationCandidateIndex", 0);
        this._openAddressValidationDialog()
      }
    } else {
      this._openAddressInvalidDialog();
    }



  }

  _formatValidationCityStateZip(address) {
    let defaultLabel = "{postalCode} {city}, {state}";
    return (this.validationCityStateZip || defaultLabel).replace(/{postalCode}/g, address.postalCode)
    .replace(/{city}/g, address.city)
    .replace(/{state}/g, address.region);
  }

  /**
   * Handler for server response after saving or editing address
   * @param {ShippingAddressDTO} address
   */
  _setShippingAddressResponse (address) {
    /**
     * @type {ShippingAddressDTO}
     */
    this.address = address;

    // Special handling needed if shipping method is pick-up-point type
    if (PickupUtil.supports(this.currentShippingMethod.Code)) {
      // If SAVE is already disabled, do not allow option to skip
      // pick-up point check
      if (this.saveBtnDisabled) {
        this._requestPickUpOptions();
      } else {
        this._openChangePickUpPointDialog();
      }
    }
    // Shipping method is not pick-up point type; proceed with saving changes
    else {
      this.set("saveBtnDisabled", false);
      this._saveDropDownValues(this.address);
    }
  }

  /**
   * Cancel out of shipping address list
   */
  _cancelShippingAddressList () {
    this.set("showPickUpOptions", false);
    this.set("saveBtnDisabled", false);

    this.frequency = this.tempFrequency;
    this.currentShippingMethod = this.tempShippingMethod;
    this.isPickUp =
      this.currentShippingMethod.Code === 'PU' ||
      this.currentShippingMethod.Code === 'FP' ||
      this.currentShippingMethod.Code === 'M3';
    this._setADRShippingValues();
    this.showShippingInformation();
    this._clearToastMessages();
  }

  /**
   * Prepares footer text that includes process date and frequency
   * @param {number} shipTimestamp
   * @param {number} shipFrequency
   * @return {String}
   */
  _computeProcessDate (shipTimestamp, shipFrequency) {
    if (!shipTimestamp || !shipFrequency) return;

    /**
     * @type {Array<{label: string, frequency: number}>}
     */
    const processDateTexts = (this.processingSummaryText instanceof Array)
      ? this.processingSummaryText
      : !!this.processingSummaryText
        ? [this.processingSummaryText]
        : [];

    const matchingFrequencyDateText = processDateTexts.find(d => d.frequency === shipFrequency);

    if (matchingFrequencyDateText) {
      const ordinalDay = moment(new Date(shipTimestamp)).locale(this.language + '_' + this.country).format('Do');
      return matchingFrequencyDateText.label.replace(/{day}/g, ordinalDay);
    }

    return '';
  }

  /**
   * Sets up the Post body
   * @returns {ShippingAddressDTO}
   */
  _getPostBody () {
    let body = {
      'shippingAddressName': this.shippingAddressName,
      'shippingAddress1': this.shippingAddress1,
      'shippingAddress2': this.shippingAddress2 || '',
      'shippingCity': this.shippingCity,
      'shippingCounty': this.shippingCounty,
      'shippingState': this.shippingState,
      'shippingPostalCode': this.shippingPostalCode,
      'shippingCountry': this.shippingCountryCode || this.countryCode,
      'shippingPhone': this.shippingPhone,
      'shippingMobilePhone': this.shippingMobilePhone,
      'shippingInstructions': this.shippingInstructions,
      'addressType': 'home',
      'shippingEmail': this.shippingEmail,
      'defaultShipping': this.isDefaultShipping
    };

    if (this.shippingCountryCode && this.shippingCountryCode !== this.countryCode) {
      body['shippingState'] = null
    }

    return body;
  }

  _getValidationBody () {
    const validationCandidate = {
      'street': this.shippingAddress1,
      'street2': this.shippingAddress2 || '',
      'city': this.shippingCity,
      'district': this.shippingCounty,
      'region': this.shippingState,
      'postalCode': this.shippingPostalCode
    };
    this.set('validationCandidate', validationCandidate);
    return validationCandidate;
  }

  _selectedValidationCandidateIndexChanged(index) {
    if (index !== null) {
      this.selectedValidationAddress = this.validationCandidates[index];
    }
  }

  _saveValidatedAddress() {
    this._setValidationResult(this.selectedValidationAddress);
    this._saveAddress();
    this._closeAddressValidationDialog();
  }

  _setValidationResult(address) {
    this.shippingAddress1 = address.street;
    this.shippingAddress2 = address.street2;
    this.shippingCity = address.city;
    this.shippingCounty = address.district;
    this.shippingState = address.region;
    this.shippingPostalCode = address.postalCode;
  }

  /**
   * Sets up the Put body
   * @returns {ShippingAddressDTO}
   */
  _getPutBody () {
    let body = this._getPostBody();
    body['shippingAlias'] = this.shippingAlias;
    body['beShippingAddressId'] = this.beShippingAddressId;
    return body;
  }

  /**
   * Sets the view to switch between different views
   * @param {String} message
   */
  _isView (prop, message) {
    return prop === message;
  }

  /**
   * Sets up frequencies list for dropdown
   */
  _getFrequencies () {
    /**
     * @type {Array<{key: number, value: string}>}
     */
    let frequencies = [{'key': 1, 'value': this.monthlyText}];
    if (this.displayBiMonthlyFrequency || this.displayBiMonthlyFrequency === undefined) {
      frequencies.push({'key': 2, 'value': this.biMonthlyText});
    }
    this.frequencies = frequencies;
  }

  /**
   * Initializes the ADR processing date selection dropdown.
   * Dropdown options are generated and an initial selection is made based on the current
   * ADR processing date.
   */
  _setProcessingDateOptions() {
    const shippingRestriction = this.shippingMethodRestrictions
      .find(r => r.code === this.currentShippingMethod.Code);

    const now = new Date();
    const isBimonthly = this.shippingDataObject.frequency === 2;

    // Compute first day of next month
    const firstOfNextMonth = new Date();
    firstOfNextMonth.setMonth(firstOfNextMonth.getMonth() + 1, 1);

    // Compute last day of this month
    const endOfThisMonth = new Date();
    endOfThisMonth.setMonth(endOfThisMonth.getMonth() + 1, 0);

    // Compute number of days in this month, and days until end of this month
    const daysInThisMonth = endOfThisMonth.getDate();
    const daysToEndOfMonth = daysInThisMonth - now.getDate();

    // Compute last day of next month, and number of days in next month
    const endOfNextMonth = new Date();
    endOfNextMonth.setMonth(endOfNextMonth.getMonth() + 2, 0);
    const daysInNextMonth = endOfNextMonth.getDate();

    // Determine whether ADR has ever shipped before,
    // whether ADR has already shipped this month, and
    // when ADR is scheduled to ship next (this month or next)
    const hasShippedBefore = !!this.shippingDataObject.lastPlacedDate;
    const lastShipDate = new Date(this.shippingDataObject.lastPlacedDate);
    const hasShippedThisMonth = hasShippedBefore && (now.getMonth() === lastShipDate.getMonth());
    const nextShipDate = new Date(this.shippingDataObject.nextDeliveryDate);
    const willNextShipThisMonth = now.getMonth() === nextShipDate.getMonth();
    const willNextShipNextMonth = firstOfNextMonth.getMonth() === nextShipDate.getMonth();

    // Default case: ADR has never yet shipped before
    // Options should start from now and run for ADR's full interval length
    let optionsStartDate = now;
    let optionsLength = daysInThisMonth + (isBimonthly ? daysInNextMonth : 0);

    if (hasShippedThisMonth) {
      // ADR has shipped this month; don't allow another scheduled shipment this month
      // Options should start next month and run for ADR's full interval length
      optionsStartDate = firstOfNextMonth;
    }
    else if (hasShippedBefore && willNextShipThisMonth) {
      // ADR has shipped before, and is scheduled to ship later this month
      // Options should start from now and run until end of this month
      optionsLength = daysToEndOfMonth
    }
    else if (hasShippedBefore && willNextShipNextMonth) {
      // ADR has shipped before, hasn't shipped this month, and is scheduled to ship next month
      // Options should start from now and run until end of next month
      optionsLength = daysToEndOfMonth + daysInNextMonth;
    }

    this.processDateOptions = createDateList({
      length: optionsLength,
      now: optionsStartDate,
      nextProcessDate: new Date(this.shippingDataObject.nextDeliveryDate),
      disabledDays: this.adrDisabledDates,
      blackoutPeriod: this.adrBlackoutPeriod,
      allowedDays: shippingRestriction
    }).map(date => ({
      timestamp: date.getTime(),
      label: this.dateDropdownLabelFor(date)
    }));
  }

  /**
   *
   * @param {Date} date
   * @returns {string}
   */
  dateDropdownLabelFor(date) {
    const momentDate = moment(date, "D")
      .locale(this.language + "_" + this.country);
    let defaultLabel = "{day} {month}";
    if (this.showMonthAsNumber) {
      defaultLabel = "{day2} / {monthNumber} / {year}"
    } else if (this.showDateAsOrdinal) {
      defaultLabel = "{ordDay} {month}";
    }

    return (this.processDateOptionText || defaultLabel)
      .replace(/{day}/g, momentDate.format("D"))
      .replace(/{day2}/g, momentDate.format("DD"))
      .replace(/{ordDay}/g, momentDate.format("Do"))
      .replace(/{month}/g, momentDate.format("MMMM"))
      .replace(/{monthNumber}/g, momentDate.format("MM"))
      .replace(/{year}/g, momentDate.format("YYYY"));
  }

  _initSelectedProcessingDate() {
    /*
    Set selected option to match day-of-month of current ship date.
    This is done because of the discrepancy between a day-of-month-based ship date selector and bi-monthly
    ADR orders. When the ADR is bi-monthly, a dropdown option of "Xth day of the month" can mean one of two
    possible dates. As such, initializing the dropdown to match the current ship date could result in a blank
    dropdown as the initial value fails to match any of its entries.
    Instead, it is initialized to the matching day-of-month, and the dropdown listener _onSelectShipDate is
    configured to treat reversion to the initial selection as a special case.
    */
    const nextDeliveryDate = this.shippingDataObject.nextDeliveryDate;
    const matchingOption = this.processDateOptions.find(o =>
      o.timestamp === nextDeliveryDate
    );

    if (matchingOption) {
      this.selectedProcessTimestamp = matchingOption.timestamp;
      this.isValidShippingDate = true;
    } else if (nextDeliveryDate && nextDeliveryDate >= new Date()) {
      this.set(
        'shippingProcessDatePlaceHolder',
        this.dateDropdownLabelFor(new Date(nextDeliveryDate))
      );
      this.isValidShippingDate = true;
    } else {
      this.isValidShippingDate = false;
    }
  }

  /**
   * Observer for processing date selection dropdown.
   * @param {number} timestamp - Selected shipping date timestamp from dropdown
   */
  _onSelectProcessingDate(timestamp) {
    if (this.processDateSelectorPristine) {
      // User has not made a selection or has reverted their selection. Revert to current ship date.
      this.requestedProcessTimestamp = this.shippingDataObject.nextDeliveryDate;
    } else {
      this.isValidShippingDate = true;
      this.requestedProcessTimestamp = timestamp;
    }
  }

  _onSelectShipFromAddressName(newShipFromAddressName) {
    this.shipFromAddressName = newShipFromAddressName;
  }

  /**
   * Initializes shipping method options
   * @private
   */
  _initShippingMethods () {
    // Get available shipping methods
    let methods = this.shippingDataObject.shippingAvailableMethods;

    // Remove any malformed shipping methods
    methods = methods.filter(m => m.Description && !m.Description.includes('?shippingMethod'));

    // Remove any shipping methods whose restrictions disallow the current shipping date
    const shippingDayOfMonth = new Date(this.shippingDataObject.nextDeliveryDate).getDate();
    methods = methods.filter(m => {
      const restriction = this.shippingMethodRestrictions.find(r => r.code === m.Code);
      return restriction ? restriction.dates.includes(shippingDayOfMonth) : true;
    });

    // Add non-pickup-location shipping methods to dropdown list
    /**
     * @type {ShippingMethod[]}
     */
    this.shippingMethods = methods.filter(m => m.Code !== 'PU' && m.Code !== 'FP' && m.Code !== 'M3');

    // If pickup locations are allowed by market config, add them to the end of the list
    let pickupLocations = methods.filter(m => m.Code === 'PU' || m.Code === 'FP' || m.Code === 'M3');
    if (this.showShippingPickupLocation) {
      this.shippingMethods = this.shippingMethods.concat(pickupLocations);
    }

    /*
    For any shipping methods with descriptions long enough to include line breaks,
    split the description for display in the dropdown.
    */
    this.shippingMethods
      .forEach(m => {
        let breakIdx = m.Description.indexOf('<br>');

        if (breakIdx > -1) {
          m.longDesc = m.Description.substring(breakIdx + 4);
          m.shortDesc = m.Description.substring(0, breakIdx);
        } else {
          // no line breaks found; use entire string
          m.shortDesc = m.Description;
          m.longDesc = '';
        }
      });

    /*
     * Bugfix of CX14-1200, working around internal state update bug in
     * ns-slotted-dropdown by forcibly toggling dropdown's selected item
     */
    this.set("idxOfShippingMethod", -1);
    setTimeout(() => {
      const smidx = this.getShippingMethodIdx();
      this.set("idxOfShippingMethod", smidx)
    }, 5);
  }

  /**
   * Observer triggered when a shipping method is selected from the dropdown list.
   * Sets the selected shipping method object according to the updated array index.
   * Sets the "isPickUp" flag to true if the selected shipping method is a Pickup
   * Location (method code "PU"), false otherwise.
   * @private
   */
  _onShippingMethodIdxChanged(idx) {
    if (typeof idx !== 'number' || idx < 0 || idx >= this.shippingMethods.length) {
      return;
    }
    /**
     * @type {ShippingMethod}
     */
    this.currentShippingMethod = this.shippingMethods[idx];
    if (PickupUtil.supports(this.currentShippingMethod.Code)) {
      if (!this.pickUpOptionSelected) {
        this.set("saveBtnDisabled", true);
        if (!this.useShipMethodsApi) {
          this._requestPickUpOptions();
        } else {
          this._showPickUpPoints();
        }
      } else {
        this.showSelectedPickUpPoint = true;
      }
    } else {
      this.set("pickUpOptionSelected", null);
      this.set("showPickUpOptions", false);
      this.set("showSelectedPickUpPoint", false);
      this.set("saveBtnDisabled", false);
    }
    this.isPickUp =
      this.currentShippingMethod.Code === 'PU' ||
      this.currentShippingMethod.Code === 'FP' ||
      this.currentShippingMethod.Code === 'M3';
    this.pickUpDescription = this.isPickUp ? this.currentShippingMethod.longDesc : '';
    this._clearToastMessages();

    this._setProcessingDateOptions();

    const nextDeliveryDayOfMonth = new Date(this.requestedProcessTimestamp).getDate();
    this.isValidShippingDate = this.processDateOptions.some(option =>
      new Date(option.timestamp).getDate() === nextDeliveryDayOfMonth
    );
  }

  /**
  * Closes frequency change modal and overlay, sets view back to original view
  */
  _saveSelectedFrequency () {
    this.$.frequencyChangeDialog.close();
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: true}}));
    this._updateAdrFrequencyDetails(this.address);
    this.view = 'Shipping Information';
  }

  /**
   * Sets up the adr shipping address body
   * @param {ShippingAddressDTO} address
   * @returns {ShippingAddress}
   */
  _getAdrShippingAddressBody (address) {
    return {
      'Name': address.shippingAddressName,
      'Address': {
        'Street1': address.shippingAddress1,
        'Street2': address.shippingAddress2,
        'City': address.shippingCity,
        'District': address.shippingCounty,
        'Region': address.shippingState,
        'PostalCode': address.shippingPostalCode,
        'CountryCode': address.shippingCountry
      },
      'Contact': [{
        'Country': '',
        'Value': {
          'value': address.shippingEmail,
          'Type': 'EMAIL'
        }
      }, {
        'Country': this.shippingPhoneCountry || address.shippingCountry,
        'Value': {
          'value': address.shippingPhone,
          'Type': 'HOMEPHONE'
        }
      }, {
        'Country': this.shippingMobilePhoneCountry || address.shippingCountry,
        'Value': {
          'value': address.shippingMobilePhone,
          'Type': 'MOBILEPHONE'
        }
      }],
      'Instructions': address.shippingInstructions
    };
  }

  /**
  * Used for frequency change cancel, closes modal and sets frequency to monthly
  */
  _closeFrequencyChangeDialog () {
    this.$.frequencyChangeDialog.close();
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: true}}));
  }

  _openInvalidShippingDateDialog() {
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: false}}));
    this.$.invalidShippingDateDialog.open();
  }

  _closeInvalidShippingDateDialog() {
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: true}}));
    this.$.invalidShippingDateDialog.close();
  }

  _openAddressValidationDialog() {
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: false}}));
    this.$.addressValidationDialog.open();
  }

  _closeAddressValidationDialog() {
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: true}}));
    this.selectedValidationAddress = null;
    this.selectedValidationCandidateIndex = null;
    this.$.addressValidationDialog.close();
  }

  _openAddressInvalidDialog() {
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: false}}));
    this.$.addressInvalidDialog.open();
  }

  _closeAddressInvalidDialog() {
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: true}}));
    this.$.addressInvalidDialog.close();
  }

  /**
   * Adds a toast message for display to the user.
   * @param {string} type - message type: "success", "warning", "error", "information"
   * @param {Object} options - options:
   * @param {string} [options.header] - message header text
   * @param {string} [options.message] - message body text
   * @param {boolean} [options.scrollIntoView = false] - flag indicating whether to scroll to the toast message
   * @param {boolean} [options.showPrintOption = false] - flag indicating whether to show a print button
   */
  _addToastMessage(type, options = {}) {
    const DEFAULT_OBJECT = {
      type: 'information',
      header: '',
      scrollIntoView: false,
      showPrintOption: false
    };
    const messageObject = Object.assign({}, DEFAULT_OBJECT, options, {type});
    this.push('toastMessages', messageObject);
  }

  /**
   * Clears all toast messages from the page.
   */
  _clearToastMessages() {
    this.set('toastMessages', []);
  }

  _handleError (err) {
    if (err instanceof NsRestError) {
      err.messages.forEach(m => {
        console.error(m.technicalMessage, m);
        this._addToastMessage('error', {
          header: m.message,
          scrollIntoView: true
        });
      });
    } else {
      console.error(err);
      this._addToastMessage('error', {
        header: err.message,
        scrollIntoView: true
      });
    }
  }

  /**
   * Observer for the pick-up point map/list toggle.
   * @param {number} tabIdx
   */
  _onChangePPTab(tabIdx) {
    if (tabIdx === 1) {
      this.ppListTabClass = "pp-hidden-tab";
      this.ppMapTabClass = "";
      this._fitMapToBounds();
    } else {
      this.ppListTabClass = "";
      this.ppMapTabClass = "pp-hidden-tab";
    }
  }

  /**
   * Observer for the `showPickUpOptions` flag.
   * @param {boolean} show
   */
  _onSetShowPickupOptions(show) {
    if (!show) return;

    // Apply short async timeout to allow DOM to add elements needed by map
    setTimeout(() => {
      if (this.pickupPointsMap) {
        this._createPickUpPointMarkers(this.pickUpOptions);
      } else {
        this._initPickupPointMap()
          .then(() => {
            this._createPickUpPointMarkers(this.pickUpOptions);
          });
      }
    }, 2);
  }

  /**
   * Triggers a retrieval of pick-up point options, based on the value of
   * `this.address`.
   *
   * Observer function `_onChangePickUpOptions` is invoked when updated
   * options are received.
   */
  _requestPickUpOptions() {
    this._putAdrService(this.address, true);
  }

  _showPickUpPoints() {
    this.set("showPickUpOptions", true);
    this.set("showSelectedPickUpPoint", false);
    this.set("pickUpOptionSelected", null);
    this._scrollToShippingMethodsSection();

    // If map has already been initialized, apply markers immediately
    if (this.shadowRoot.querySelector("#googleMapContainer")) {
      // Apply short async timeout to allow DOM to render pick-up options list
      // before map markers are created.
      setTimeout(() => this._createPickUpPointMarkers(this.pickUpOptions), 5);
    }
  }

  /**
   * Observer for `this.pickUpOptions`. Invoked when new pick-up options are
   * received.
   * @param {PickupPoint[]} points
   */
  _onChangePickUpOptions(points) {
    if (!points
      || !this.currentShippingMethod
      || !PickupUtil.supports(this.currentShippingMethod.Code)) {
        if (!points) this.set('shippingDataObject.pickUpOptions', undefined);
        return;
      }

    if (points.length === 0) {
      this.autoShowPickupPoints = false
      this.showPickUpOptions = false;
      this._openPickUpPointsNotFoundDialog();

      return;
    }

    if (this.useShipMethodsApi) {
      this._createPickUpPointMarkers(this.pickUpOptions);
    } else {
      this._showPickUpPoints();
    }
  }

  /**
   * Observer triggered when user chooses to change their chosen pick-up point.
   */
  _onSelectChangePP() {
    this.set("saveBtnDisabled", true);
    if (!this.useShipMethodsApi) {
      this._requestPickUpOptions();
    } else {
      this._showPickUpPoints();
    }
  }

  /**
   * observer triggered when updating feature flags object
   */

  _featureFlagsUpdated() {
    if (this.featureFlags.cx14_1550_use_month_number && !this.showMonthAsNumber) {
      //need to reset config values because this is returning after the initialization of the object
      this._setConfigurations()
    }
  }
  /**
   *
   * @param {boolean} show
   */
  async _initPickupPointMap() {
    // Temporarily select map tab to give map's parent element width
    this.set("selectedPPTab", 1);
    const $el = this.shadowRoot.querySelector("#googleMapContainer");
    if (!this.pickupPointsMap && $el) {
      const {Map: gmap, InfoWindow} = await google.maps.importLibrary('maps');
      const {AdvancedMarkerElement, PinElement} = await google.maps.importLibrary('marker');
      this.InfoWindow = InfoWindow;
      this.AdvancedMarkerElement = AdvancedMarkerElement;
      this.PinElement = PinElement;
      this.pickupPointsMap = new gmap($el, {
        zoom: 6,
        mapId: 'pickUpLocations'
    });
    }
    // Switch back to default tab
    this.set("selectedPPTab", 0);
  }

  getMarkerInfo(pup, pupIndex) {
    return `
        <div ng-non-bindable="">
            <div class="pickupPointInfo">
                <h3 class="section-result-title">${pup.name}</h3>
                <div class="pickupPointAddress">${pup.street}</div>
                <div class="pickupPointAddress">${pup.city}</div>
                <div class="pickupPointAddress">${pup.carrier}</div>
                <label style="display: block; text-align: left;">${pup.openingHours}</label>
                <button class="hit-solid thin"
                    onclick="window.selectPickupPoint(${pupIndex})">
                    Pickup Fix This
                </button>
            </div>
        </div>
    `;
  }

  /**
   *
   * @param {PickupPoint} pickupPoints
   */
  _createPickUpPointMarkers(pickupPoints) {
    this._initPickupPointMap();
    if (Array.isArray(pickupPoints) && this.pickupPointsMap) {
      this.mapMarkers.forEach((m) => m.map = null);
      this.bounds = pickupPoints.reduce((bounds, pup) => {
        return bounds.extend({
            lat: pup.latitude,
            lng: pup.longitude
        });
      }, new google.maps.LatLngBounds());

      // this.pickupPointsMap.fitBounds(bounds);

      const infoWindow = new this.InfoWindow();
      window.selectPickupPoint = (index) => {
        this.selectPickUpOption({
          model: { item: pickupPoints[index] }
        })
      };
      pickupPoints.forEach((pup, pupIndex) => {
          const pin = new this.PinElement({
              glyph: `${pupIndex + 1}`,
              borderColor: 'black',
              background: '#4087AD',
              glyphColor: 'white'
          });
          const marker = new this.AdvancedMarkerElement({
              map: this.pickupPointsMap,
              position: {lat: pup.latitude, lng: pup.longitude},
              content: pin.element
              // title: "Uluru",
          });
          marker.addListener('click', () => {
              infoWindow.close();
              infoWindow.setContent(this.getMarkerInfo(pup, pupIndex));
              infoWindow.open(marker.map, marker);
          });
          this.mapMarkers.push(marker);
      });
    }
  }

  _createPickUpPointMarkers_orig(pickupPoints) {
    // Clear previous markers, if any
    this.mapMarkers.forEach(m => m.setMap(null));

    if (!Array.isArray(pickupPoints) || pickupPoints.length === 0) {
      return;
    }

    this.mapMarkers = PickupUtil.markers(pickupPoints)
      .map((markerData, index) => {
        const mapMarker = new google.maps.Marker({
          icon: markerData.icon,
          position: {
            lat: markerData.position[0],
            lng: markerData.position[1]
          },
          map: this.pickupPointsMap
        });

        const $pickupInfo = this.shadowRoot
          .querySelector(`#pickUpOption_${markerData.id}`);

        if ($pickupInfo) {
          const $pickupInfoClone = $pickupInfo.cloneNode(true);
          Array.from($pickupInfoClone.children)
            .filter(e => e.tagName === "NS-BUTTON")
            .forEach(e => e.addEventListener("click", () =>
              this.selectPickUpOption({
                model: { item: pickupPoints[index] }
              })
            ));
          /*
          On mouseover or click of the marker icon,
          show pickup point info in Map Info Window
          */
          const openInfoWindow = () => {
            this.mapInfoWindow.setContent($pickupInfoClone);
            this.mapInfoWindow.open(this.pickupPointsMap, mapMarker);
          };
          mapMarker.addListener('mouseover', openInfoWindow);
          mapMarker.addListener('click', openInfoWindow);
        }

        return mapMarker;
      });

    this._fitMapToBounds();
  }

  _fitMapToBounds() {
    if (this.bounds) this.pickupPointsMap.fitBounds(this.bounds);

    // Abort if data and/or Maps plugin are missing
    // if (!this.pickUpOptions
    //   || this.pickUpOptions.length === 0
    //   || !google
    //   || !this.pickupPointsMap) return;

    // const mapBounds = PickupUtil.bounds(this.pickUpOptions);
    // const padding = 8; // Additional padding for map bounds (px)
    // this.pickupPointsMap.fitBounds(mapBounds, padding);
  }

  /**
   * On-tap function for pick up option SELECT Button
   * @param {event} e
   */
  selectPickUpOption (e) {
    this.set('pickUpOptionSelected', e.model.item);
    this._pickUpOptionSelectedChanged();
    this.set("saveBtnDisabled", false);
    this.set("showPickUpOptions", false);
  }

  /**
   * On-tap function for selected pick up option CHANGE button
   */
  _changePickUpOptionSelected () {
    this._saveDropDownValues(this.address, null, true);
    this.showSelectedPickUpPoint = false;
  }

  _openChangePickUpPointDialog() {
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: false}}));
    this.$.changePickUpPointDialog.open();
  }

  _closeChangePickUpPointDialog() {
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: true}}));
    this.$.changePickUpPointDialog.close();
    this.set("saveBtnDisabled", false);
    this._saveDropDownValues(this.address);
  }

  /**
   *
   * Observer for "Yes" button of "Change pick-up point?" popup.
   */
  _onChooseChangePickUpPoint() {
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: true}}));
    this.$.changePickUpPointDialog.close();
    this.autoShowPickupPoints = true;
    this.set("saveBtnDisabled", false);
    this._saveDropDownValues(this.address);
  }

  _openPickUpPointsNotFoundDialog() {
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: false}}));
    this.$.pickUpPointsNotFoundDialog.open();
  }

  _closePickUpPointsNotFoundDialog() {
    this.dispatchEvent(new CustomEvent('closeAdpWrapperOverlay', {detail: {closeOverlay: true}}));
    this.$.pickUpPointsNotFoundDialog.close();
  }

  _scrollToShippingMethodsSection() {
    const el = this.shadowRoot.querySelector("shipping-methods-tag");
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }
}

customElements.define(NsShipping.is, NsShipping);

/**
 * Returns `true` if the two addresses are equivalent, `false` otherwise.
 * @param {ShippingAddressDTO} address1
 * @param {ShippingAddressDTO} address2
 * @returns {boolean}
 */
function shippingAddressesMatch(address1, address2) {
  const match = (a, b) => (!a && !b) || (a === b);
  return ([
    "shippingAddressName",
    "shippingAddress1",
    "shippingAddress2",
    "shippingCity",
    "shippingCounty",
    "shippingState",
    "shippingPostalCode",
    "shippingCountry"
  ]).every(p => match(address1[p], address2[p]));
}

/* ==================== DATA TYPE DOCUMENTATION ==================== */

/**
 * @typedef ShippingDataObject
 * @property {string} updateType
 * @property {number} lastPlacedDate
 * @property {number} nextDeliveryDate
 * @property {string} shippingId
 * @property {string} shipToPhoneCountry
 * @property {string} shipToPhone
 * @property {string} shipToMobilePhoneCountry
 * @property {string} shipToMobilePhone
 * @property {string} shipToEmail
 * @property {number} frequency
 * @property {string} currentShippingAddressName
 * @property {ShippingSection} shipping
 * @property {Address} currentShippingAddress
 * @property {ShippingMethod[]} shippingAvailableMethods
 * @property {string} timeStamp
 * @property {string} orderId
 * @property {PickupPoint[]} pickUpOptions
 * @property {{
 * name: string,
 * street: string,
 * city: string,
 * postalCode: string
 * }} pickUpOptionSelected
 */

/**
 * @typedef ShippingSection
 * @property {ShippingInformation} shipping
 * @property {ShippingMethod[]} shippingMethods
 */

/**
 * Object representing the shipping information applied to an order.
 * @typedef ShippingInformation
 * @property {Array<ShippingParty>} ShippingParty List of shipping entities
 * associated with the order.
 * @property {ShippingMethod} ShippingMethod Shipping method applied to the
 * order.
 * @property {Array<Tracking>} Tracking List of order tracking entries
 * associated with this order, if applicable.
 */

/**
 * Object representing a shipping entity.
 * @typedef ShippingParty
 * @property {string} ShippingMemberID ID of the user, distributor, or
 * business entity associated with this shipping entity.
 * @property {string} Type Type of shipping entity.
 * @property {ShippingAddress} ShippingAddress Shipping address associated
 * with this entity.
 * @property {Array<CustomField>} Custom List of custom data fields applied to
 * this shipping entity.
 */

/**
 * Object representing a shipping address.
 * @typedef ShippingAddress
 * @property {string} [ID] ID of this shipping address. Applicable only to
 * saved shipping addresses.
 * @property {string} Name Name of shipping recipient.
 * @property {string} [CareOfName] Name of "care of" recipient, if applicable.
 * @property {Address} Address Physical address.
 * @property {Array<ShippingContact>} Contact List of contact information
 * entries for this shipping address.
 * @property {string} [Instructions] Shipping instructions.
 */

/**
 * Object representing a physical address.
 * @typedef Address
 * @property {string} Street1 Street address line 1.
 * @property {string} Street2 Street address line 2.
 * @property {string} City Name of the city, town, or equivalent.
 * @property {string} District Name of the district, county, or equivalent.
 * @property {string} [DistrictDesc] Description of the district, county, or
 * equivalent.
 * @property {string} Region Name of the region, state, or equivalent.
 * @property {string} [RegionDesc] Description of the region, state, or
 * equivalent.
 * @property {string} PostalCode Postal or zip code.
 * @property {string} CountryCode Country code.
 * @property {string} [CountryCodeDesc] Description or name of the country.
 * @property {Array<InternationalAddress} [InternationalAddresses] List of
 * international addresses associated with this address, if applicable.
 */

/**
 *
 * @typedef InternationalAddress
 * @property {string} Type
 * @property {string} Street1
 * @property {string} Street2
 * @property {string} City
 * @property {string} District
 * @property {string} [DistrictDesc]
 * @property {string} Region
 * @property {string} [RegionDesc]
 * @property {string} PostalCode
 * @property {string} CountryCode
 * @property {string} [CountryCodeDesc]
 */

/**
 * Object representing a shipping recipient's contact information.
 * @typedef ShippingContact
 * @property {string} Country Country code of the country this contact
 * information applies to.
 * @property {ShippingContactValue} Value Contact information data.
 */

/**
 * Object representing contact information data.
 * @typedef ShippingContactValue
 * @property {string} value Contact information value.
 * @property {string} Type Contact information type: "WORKPHONE", "HOMEPHONE",
 * "MOBILEPHONE", "FAX", "EMAIL".
 */

/**
 * Object representing a shipping method.
 * @typedef ShippingMethod
 * @property {string} Code Shipping method code.
 * @property {boolean} Default Flag indicating whether this is the default
 * shipping method to apply.
 * @property {string} Description Description of this shipping method. May be
 * a plain string or html rich text.
 * @property {number} Cost Shipping cost.
 * @property {string} PickUpLocation ID code of the pickup location associated
 * with this shipping method. Applicable only for pickup location shipping
 * methods (e.g. code "PU").
 * @property {string} Currency Currency code for the shipping cost.
 */

/**
 * Object representing the tracking information associated with an order
 * shipment.
 * @typedef Tracking
 * @property {number} ShipDate
 * @property {string} Carrier
 * @property {string} TrackNumber
 * @property {string} TrackURL
 */

/**
 * Object representing a Pickup Point location.
 * @typedef PickupPoint
 * @property {string} id - ID of the Pickup Point location
 * @property {string} name - Name of the Pickup Point location
 * @property {string} [locker] - Locker number (if applicable)
 * @property {string} street - Street address
 * @property {string} city - City
 * @property {string} postalCode - Postal/Zip code
 * @property {string[]} openingHours - List of opening hours for the Pickup Point location
 * @property {string} distance - Distance of the Pickup Point from the reference address
 * @property {string} unit - Unit of measurement for the distance figure
 * @property {number} latitude - Latitude of the Pickup Point location
 * @property {number} longitude - Longitude of the Pickup Point location
 * @property {string} icon - Marker icon URL
 */

/**
 * Pickup Point map marker data.
 * @typedef PickupPointMarker
 * @property {number} index - Index of the Pickup Point in the list
 * @property {string} icon - Marker icon URL
 * @property {string} id - ID of the Pickup Point location
 * @property {string} name - Name of the Pickup Point location
 * @property {string} [locker] - Locker number (if applicable)
 * @property {string} street - Street address
 * @property {string} city - City
 * @property {string} postalCode - Postal/Zip code
 * @property {string[]} openingHours - List of opening hours for the Pickup Point location
 * @property {string} distance - Distance of the Pickup Point from the reference address
 * @property {number[]} position - Latitude-longitude pair
 */

/**
 * Object representing a shipping address saved to a user's profile.
 * @typedef ShippingAddressDTO
 * @property {number} [beShippingAddressId] ID of this address. This value is
 * added by the server and should not be altered by the client.
 * @property {string} addressType
 * @property {boolean} [defaultShipping] Flag indicating whether this is the
 * user's default shipping address.
 * @property {string} shippingAddressName
 * @property {string} shippingAddress1
 * @property {string} shippingAddress2
 * @property {string} shippingCity
 * @property {string} shippingCounty
 * @property {string} shippingState
 * @property {string} shippingPostalCode
 * @property {string} shippingCountry
 * @property {string} [shippingPhone]
 * @property {string} [shippingPhoneCountry]
 * @property {string} [shippingMobilePhone]
 * @property {string} [shippingMobilePhoneCountry]
 * @property {string} [shippingEmail]
 * @property {string} [shippingInstructions]
 * @property {string} [shippingAlias]
 */

/**
 * Object representing a custom data field used in communication with Nu Skin
 * Shop APIs. This is used to pass a data value from client to server or
 * vice-versa, where the value in question doesn't fit into the existing API
 * data fields.
 * @typedef CustomField
 * @property {string} Key Custom field key name.
 * @property {string} Value Custom field value.
 * @property {string} Type Custom field type.
 */
